/**
 * Client-side validation for the tenant invite form (REGTA-63).
 *
 * Kept as a pure, dependency-free module so it can be unit-tested in
 * isolation and shared by the form's inline field errors and its
 * submit-gate. The rules deliberately mirror the backend `users.invite`
 * zod schema (email, name 1–200, group_ids min 1) — the backend stays the
 * source of truth; this only surfaces the same failures earlier.
 */

export interface InviteFormValues {
  name: string;
  email: string;
  selectedGroupIds: readonly string[];
}

export interface InviteFieldErrors {
  /** Swedish, user-facing message, or null when the field is valid. */
  name: string | null;
  email: string | null;
  groups: string | null;
}

const NAME_MAX = 200;

// Intentionally loose: "something@something.something". The backend's zod
// `.email()` is authoritative; this only catches the obvious typos a user
// can see and fix before we bother the server.
const EMAIL_RE = /.+@.+\..+/;

export function inviteFieldErrors(values: InviteFormValues): InviteFieldErrors {
  const name = values.name.trim();
  const email = values.email.trim();

  return {
    name:
      name.length === 0
        ? 'Ange namn på användaren.'
        : name.length > NAME_MAX
        ? `Namnet får vara högst ${NAME_MAX} tecken.`
        : null,
    email:
      email.length === 0
        ? 'Ange en e-postadress.'
        : !EMAIL_RE.test(email)
        ? 'Ange en giltig e-postadress.'
        : null,
    groups:
      values.selectedGroupIds.length === 0
        ? 'Välj minst en grupp för användaren.'
        : null,
  };
}

export function hasInviteErrors(errors: InviteFieldErrors): boolean {
  return Boolean(errors.name || errors.email || errors.groups);
}
